// TAS-274: true once the big screen has shown this wine and emitted
// `wine-revealed`. In TV mode a phone holds its reveal until then, so nobody
// reads the name off their phone before the room sees it on the TV.
//
// Reads the event on mount (a phone that arrives late catches up), listens for
// the insert, and re-asks after a reconnect or a phone waking up — a sleeping
// phone's socket often survives, and a missed insert would otherwise strand it.
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@tasted/utils/supabase';
import { resilientChannel } from '../utils/realtime';
import { useCatchUpOnRecovery } from './useCatchUpOnRecovery';

export function useTvWineRevealed(sessionCode, wineId, { enabled = true } = {}) {
  const [revealed, setRevealed] = useState(false);
  const recoverRef = useRef(null);

  useEffect(() => {
    setRevealed(false);
    if (!enabled || !sessionCode || !wineId) return undefined;

    const check = () => {
      supabase
        .from('game_events_v2')
        .select('payload')
        .eq('session_id', sessionCode)
        .eq('event_type', 'wine-revealed')
        .then(({ data }) => {
          if ((data || []).some((r) => String(r.payload?.wineId) === String(wineId))) setRevealed(true);
        });
    };
    check();
    recoverRef.current = check;

    const dispose = resilientChannel({
      topic: () => `tv-wine-revealed-${sessionCode}-${wineId}:${Date.now()}`,
      meta: { sessionCode, context: 'tv-wine-revealed' },
      setup: (channel) => channel.on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'game_events_v2', filter: `session_id=eq.${sessionCode}` },
        (payload) => {
          if (
            payload.new?.event_type === 'wine-revealed' &&
            String(payload.new.payload?.wineId) === String(wineId)
          ) setRevealed(true);
        },
      ),
      onStatus: (status) => { if (status === 'SUBSCRIBED') check(); },
    });
    return () => { recoverRef.current = null; dispose(); };
  }, [enabled, sessionCode, wineId]);

  useCatchUpOnRecovery(
    Boolean(enabled && sessionCode && wineId && !revealed),
    useCallback(() => recoverRef.current?.(), []),
  );

  return revealed;
}
